import {
  DndContext,
  DragOverlay,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragMoveEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  ChevronsLeft,
  ChevronsRight,
  GripVertical,
  ListOrdered,
  Star,
  StarOff,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { NavLink } from 'react-router-dom';
import type { NavItem } from '@/features/shell/buildNavItems';
import { Tooltip } from '@/shared/ui/Tooltip/Tooltip';
import styles from './Sidebar.module.css';

type SidebarProps = {
  items: NavItem[];
  retraida: boolean;
  onToggleRetraida: () => void;
  onReordenar?: (codigosOrdenados: number[]) => void;
  /** Remove atalho dos favoritos (ex.: soltar fora da lista). */
  onRemoverAtalho?: (codigoModulo: number) => void;
};

function pontoForaDoRetangulo(
  x: number,
  y: number,
  rect: DOMRect,
  margem = 12,
): boolean {
  return (
    x < rect.left - margem ||
    x > rect.right + margem ||
    y < rect.top - margem ||
    y > rect.bottom + margem
  );
}

function SortableNavItem({
  item,
  retraida,
  modoReordenar,
}: {
  item: NavItem;
  retraida: boolean;
  modoReordenar: boolean;
}) {
  const Icon = item.icon;
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.codigoModulo,
    disabled: !modoReordenar,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const link = (
    <NavLink
      to={item.path}
      end={item.path === '/'}
      className={({ isActive }) => `${styles.link} ${isActive ? styles.active : ''}`}
      onClick={(e) => {
        if (modoReordenar) e.preventDefault();
      }}
      tabIndex={modoReordenar ? -1 : undefined}
      aria-disabled={modoReordenar || undefined}
      draggable={false}
      data-alvo={alvoMenu(item.path)}
      data-alvo-rotulo={`Menu ${item.label}`}
    >
      <Icon size={20} aria-hidden />
      <span className={styles.label}>{item.label}</span>
      {item.badge != null && item.badge > 0 ? (
        <span className={styles.badge} aria-label={`${item.badge} não lidas`}>
          {item.badge > 99 ? '99+' : item.badge}
        </span>
      ) : null}
    </NavLink>
  );

  const content = modoReordenar ? (
    <div
      className={`${styles.row} ${styles.rowReordenando} ${
        isDragging ? styles.rowDragging : ''
      }`}
    >
      <button
        type="button"
        className={styles.grip}
        aria-label={`Arrastar ${item.label}`}
        {...listeners}
        {...attributes}
      >
        <GripVertical size={18} aria-hidden />
      </button>
      {link}
    </div>
  ) : (
    link
  );

  return (
    <li
      ref={setNodeRef}
      style={style}
      className={`${styles.item} ${isDragging ? styles.itemDragging : ''} ${
        modoReordenar ? styles.itemReordenando : ''
      }`}
    >
      {retraida ? (
        <Tooltip
          content={item.label}
          placement="right"
          delayMs={100}
          triggerClassName={styles.tipTrigger}
        >
          {content}
        </Tooltip>
      ) : (
        content
      )}
    </li>
  );
}

function OverlayItem({ item, removendo }: { item: NavItem; removendo: boolean }) {
  const Icon = item.icon;
  return (
    <div
      className={`${styles.row} ${styles.rowReordenando} ${styles.rowOverlay} ${
        removendo ? styles.rowOverlayRemover : ''
      }`}
    >
      <span className={styles.grip} aria-hidden>
        {removendo ? <StarOff size={18} /> : <GripVertical size={18} />}
      </span>
      <div
        className={`${styles.link} ${styles.linkOverlay} ${
          removendo ? styles.linkOverlayRemover : ''
        }`}
      >
        <Icon size={20} aria-hidden />
        <span className={styles.label}>{item.label}</span>
      </div>
    </div>
  );
}

/** Id para destacar o link na tela: /relatorios/acessos -> menu-relatorios-acessos. */
function alvoMenu(path: string): string {
  const slug = path.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  return `menu-${slug || 'inicio'}`.slice(0, 40);
}

export function Sidebar({
  items,
  retraida,
  onToggleRetraida,
  onReordenar,
  onRemoverAtalho,
}: SidebarProps) {
  const [modoReordenar, setModoReordenar] = useState(false);
  const [draggingId, setDraggingId] = useState<number | null>(null);
  const [foraDaLista, setForaDaLista] = useState(false);
  const listaRef = useRef<HTMLUListElement>(null);
  const foraRef = useRef(false);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
  );

  useEffect(() => {
    if (retraida) {
      setModoReordenar(false);
      setDraggingId(null);
      setForaDaLista(false);
      foraRef.current = false;
    }
  }, [retraida]);

  const atualizarFora = (event: DragMoveEvent | DragStartEvent) => {
    const lista = listaRef.current;
    const translated = event.active.rect.current.translated;
    if (!lista || !translated) {
      foraRef.current = false;
      setForaDaLista(false);
      return;
    }
    const cx = translated.left + translated.width / 2;
    const cy = translated.top + translated.height / 2;
    const fora = pontoForaDoRetangulo(cx, cy, lista.getBoundingClientRect());
    foraRef.current = fora;
    setForaDaLista(fora);
  };

  const onDragStart = (event: DragStartEvent) => {
    setDraggingId(Number(event.active.id));
    foraRef.current = false;
    setForaDaLista(false);
  };

  const onDragMove = (event: DragMoveEvent) => {
    atualizarFora(event);
  };

  const onDragEnd = (event: DragEndEvent) => {
    const codigo = Number(event.active.id);
    const remover = foraRef.current;
    setDraggingId(null);
    setForaDaLista(false);
    foraRef.current = false;

    if (remover) {
      onRemoverAtalho?.(codigo);
      return;
    }

    const { active, over } = event;
    if (!over || active.id === over.id || !onReordenar) return;
    const oldIndex = items.findIndex((i) => i.codigoModulo === active.id);
    const newIndex = items.findIndex((i) => i.codigoModulo === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    const moved = arrayMove(items, oldIndex, newIndex);
    onReordenar(moved.map((i) => i.codigoModulo));
  };

  const onDragCancel = () => {
    setDraggingId(null);
    setForaDaLista(false);
    foraRef.current = false;
  };

  const draggingItem =
    draggingId == null ? null : items.find((i) => i.codigoModulo === draggingId) ?? null;

  const lista = (
    <ul ref={listaRef} className={styles.list}>
      {items.map((item) => (
        <SortableNavItem
          key={item.path}
          item={item}
          retraida={retraida}
          modoReordenar={modoReordenar}
        />
      ))}
    </ul>
  );

  return (
    <aside className={`${styles.sidebar} ${retraida ? styles.retraida : ''}`}>
      <nav className={styles.nav} aria-label="Atalhos favoritos">
        {items.length === 0 ? (
          <div className={styles.vazio}>
            <Star size={20} className={styles.vazioIcon} aria-hidden />
            {!retraida ? (
              <>
                <p>Nenhum atalho favorito.</p>
                <p className={styles.vazioDica}>
                  Use o menu ao lado do nome da escola para escolher módulos.
                </p>
              </>
            ) : null}
          </div>
        ) : modoReordenar ? (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={onDragStart}
            onDragMove={onDragMove}
            onDragEnd={onDragEnd}
            onDragCancel={onDragCancel}
          >
            <p className={`${styles.dicaReordenar} ${foraDaLista ? styles.dicaRemover : ''}`}>
              {foraDaLista
                ? 'Solte para remover dos favoritos'
                : 'Arraste para reordenar · solte fora para remover'}
            </p>
            <SortableContext
              items={items.map((i) => i.codigoModulo)}
              strategy={verticalListSortingStrategy}
            >
              {lista}
            </SortableContext>
            <DragOverlay dropAnimation={null}>
              {draggingItem ? (
                <OverlayItem item={draggingItem} removendo={foraDaLista} />
              ) : null}
            </DragOverlay>
          </DndContext>
        ) : (
          lista
        )}
      </nav>

      <footer className={styles.footer}>
        {onReordenar && !retraida ? (
          <Tooltip
            content={modoReordenar ? 'Concluir' : 'Reordenar atalhos'}
            placement="right"
            delayMs={100}
          >
            <button
              type="button"
              className={`${styles.toggleBtn} ${modoReordenar ? styles.toggleBtnAtivo : ''}`}
              onClick={() => setModoReordenar((v) => !v)}
              aria-label={modoReordenar ? 'Concluir reordenação' : 'Reordenar atalhos'}
              aria-pressed={modoReordenar}
            >
              <ListOrdered size={18} aria-hidden />
            </button>
          </Tooltip>
        ) : null}
        <Tooltip
          content={retraida ? 'Expandir menu' : 'Retrair menu'}
          placement="right"
          delayMs={100}
        >
          <button
            type="button"
            className={styles.toggleBtn}
            onClick={onToggleRetraida}
            aria-label={retraida ? 'Expandir menu lateral' : 'Retrair menu lateral'}
            aria-expanded={!retraida}
          >
            {retraida ? (
              <ChevronsRight size={18} aria-hidden />
            ) : (
              <ChevronsLeft size={18} aria-hidden />
            )}
          </button>
        </Tooltip>
      </footer>
    </aside>
  );
}
